/**
 * View Profile Store
 *
 * Tracks which view profile is active per diagram type and exposes the
 * current scoring result. The store is a Zustand singleton shared across
 * all canvas instances; switching profiles is instant (re-evaluates
 * scoring on the next selector read via memo).
 *
 * UI components read `activeProfileId` to know which profile is selected
 * and call `setActiveProfile(id)` to switch. The scoring result itself is
 * computed on demand by the canvas via `evaluateProfile()` — this store
 * just holds the selection.
 *
 * Persistence: sessionStorage (NOT localStorage). Profile selection is
 * ephemeral UI preference, and using sessionStorage avoids the
 * cross-user-on-shared-device leak that localStorage has — when a
 * different user opens a new tab, they get a fresh selection rather
 * than seeing the previous user's. Within a single tab session the
 * selection survives reloads, which is what users actually want.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface ViewProfileState {
  /** Active profile ID keyed by diagram type (so each open diagram
   *  remembers its last-used profile). */
  activeByDiagramType: Record<string, string>;

  /** Highlighted rule id within the legend — clicking a legend entry
   *  dims everything that doesn't match it, focusing the user on a
   *  single rule's matches. */
  focusedRuleId: string | null;

  setActiveProfile: (diagramType: string, profileId: string) => void;
  getActiveProfile: (diagramType: string) => string | null;
  setFocusedRule: (ruleId: string | null) => void;
  clearFocusedRule: () => void;
}

export const useViewProfileStore = create<ViewProfileState>()(
  persist(
    (set, get) => ({
      activeByDiagramType: {},
      focusedRuleId: null,

      setActiveProfile: (diagramType, profileId) => {
        set((state) => ({
          activeByDiagramType: {
            ...state.activeByDiagramType,
            [diagramType]: profileId,
          },
          // Clear focus when switching profiles
          focusedRuleId: null,
        }));
      },

      getActiveProfile: (diagramType) => {
        return get().activeByDiagramType[diagramType] ?? null;
      },

      setFocusedRule: (ruleId) => set({ focusedRuleId: ruleId }),
      clearFocusedRule: () => set({ focusedRuleId: null }),
    }),
    {
      name: 'view-profile-store',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({ activeByDiagramType: state.activeByDiagramType }),
    },
  ),
);
